import React, { useEffect, useRef, useState } from 'react';
import HintToast from './HintToast';

const Spirograph: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const [showHint, setShowHint] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Reset state when component mounts
    setShowHint(true);
    setHasScrolled(false);

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Spirograph parameters
    const R = 150; // Radius of the large circle
    const r = 77;  // Radius of the rolling circle
    const d = 100; // Distance from the center of the rolling circle

    let hue = 0; // For color cycling

    // Helper function to calculate GCD
    const gcd = (a: number, b: number): number => {
      return b === 0 ? a : gcd(b, a % b);
    };

    // Calculate the total height of the document based on tMax
    const tMax = 2 * Math.PI * (R / gcd(R, r));
    const scrollHeight = window.innerHeight * (tMax / 4); // Decrease total height for faster drawing

    const drawSpirograph = (t: number) => {
      const step = 0.05; // Larger increment for faster drawing

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.beginPath();
      ctx.lineWidth = 2;

      for (let angle = 0; angle < t; angle += step) {
        const x = centerX + (R - r) * Math.cos(angle) + d * Math.cos((R - r) / r * angle);
        const y = centerY + (R - r) * Math.sin(angle) - d * Math.sin((R - r) / r * angle);

        if (angle === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      hue = (hue + 1) % 360;
      ctx.strokeStyle = `hsl(${hue}, 70%, 50%)`;
      ctx.stroke();
    };

    const onScroll = () => {
      if (!hasScrolled) {
        setHasScrolled(true);
        setShowHint(false);
      }
      
      const scrollPosition = window.scrollY;
      const t = (scrollPosition / scrollHeight) * tMax; // Map scroll position to animation time
      drawSpirograph(t);
    };

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // Add event listeners
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', handleResize);

    // Reset scroll position to top
    window.scrollTo(0, 0);

    // Draw initial frame
    drawSpirograph(0);

    // Cleanup function
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []); // Empty dependency array to run only on mount

  // Calculate height for the spacer div
  const getSpacerHeight = () => {
    if (typeof window === 'undefined') return '100vh';
    const R = 150;
    const r = 77;
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    const tMax = 2 * Math.PI * (R / gcd(R, r));
    return `${window.innerHeight * (tMax / 4)}px`;
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1000
        }}
      />
      
      {/* Scroll hint - bottom banner */}
      {showHint && <HintToast text="Scroll to draw" />}
      
      <div 
        style={{
          width: '100%',
          height: getSpacerHeight()
        }}
      />
    </>
  );
};

export default Spirograph; 